import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import { fabric } from 'fabric';
import { generateUUID } from '../../utils/uuid';
import { loadCurrentDesign, SavedDesign } from '../../utils/storage';

// Types
interface Size {
  width: number;
  height: number;
}

interface Element {
  id: string;
  type: 'image' | 'text' | 'shape';
  content: string;
  position: { x: number; y: number };
  size?: { width: number; height: number };
  style?: React.CSSProperties;
  rotation?: number;
  crop?: { unit: '%'; x: number; y: number; width: number; height: number };
  originalSize?: { width: number; height: number };
}

interface DesignState {
  id: string;
  designName: string;
  elements: Element[];
  size: Size;
  canvasShape: string;
  quantity: number;
  finish: 'matte' | 'glossy';
  notes: string;
  backgroundColor: string;
  canvas: fabric.Canvas | null;
  lastModified: string;
  created: string;
}

// Load initial state from saved design if available
const savedDesign = loadCurrentDesign();

const initialState: DesignState = {
  id: savedDesign?.id || generateUUID(),
  designName: savedDesign?.designName || 'Untitled Design',
  elements: [],
  size: savedDesign?.size || { width: 3, height: 3 },
  canvasShape: savedDesign?.canvasShape || 'square',
  quantity: savedDesign?.quantity || 50,
  finish: savedDesign?.finish || 'matte',
  notes: '',
  backgroundColor: savedDesign?.backgroundColor || '#ffffff',
  canvas: null,
  lastModified: savedDesign?.lastModified || new Date().toISOString(),
  created: savedDesign?.created || new Date().toISOString()
};

export const designSlice = createSlice({
  name: 'design',
  initialState,
  reducers: {
    // Canvas operations
    setCanvas: (state, action: PayloadAction<fabric.Canvas | null>) => {
      state.canvas = action.payload;
    },
    
    // Element operations
    addElement: (state, action: PayloadAction<Element>) => {
      state.elements.push(action.payload);
      state.lastModified = new Date().toISOString();
    },
    updateElement: (state, action: PayloadAction<{id: string, updates: Partial<Element>}>) => {
      const { id, updates } = action.payload;
      const index = state.elements.findIndex(el => el.id === id);
      if (index !== -1) {
        state.elements[index] = { ...state.elements[index], ...updates };
        state.lastModified = new Date().toISOString();
      }
    },
    deleteElement: (state, action: PayloadAction<string>) => {
      state.elements = state.elements.filter(el => el.id !== action.payload);
      state.lastModified = new Date().toISOString();
    },
    duplicateElement: (state, action: PayloadAction<string>) => {
      const element = state.elements.find(el => el.id === action.payload);
      if (element) {
        const newElement = {
          ...element,
          id: `${element.type}-${Date.now()}`,
          position: {
            x: element.position.x + 20,
            y: element.position.y + 20,
          }
        };
        state.elements.push(newElement);
        state.lastModified = new Date().toISOString();
      }
    },
    
    // Design attribute operations
    setDesignName: (state, action: PayloadAction<string>) => {
      state.designName = action.payload;
      state.lastModified = new Date().toISOString();
    },
    setSize: (state, action: PayloadAction<Size>) => {
      state.size = action.payload;
      state.lastModified = new Date().toISOString();
    },
    updateDimension: (state, action: PayloadAction<{dimension: 'width' | 'height', value: number}>) => {
      const { dimension, value } = action.payload;
      const roundedValue = Math.round(value);
      state.size = { ...state.size, [dimension]: roundedValue };
      state.lastModified = new Date().toISOString();
    },
    setCanvasShape: (state, action: PayloadAction<string>) => {
      state.canvasShape = action.payload;
      state.lastModified = new Date().toISOString();
    },
    setQuantity: (state, action: PayloadAction<number>) => {
      state.quantity = action.payload;
      state.lastModified = new Date().toISOString();
    },
    setFinish: (state, action: PayloadAction<'matte' | 'glossy'>) => {
      state.finish = action.payload;
      state.lastModified = new Date().toISOString();
    },
    setNotes: (state, action: PayloadAction<string>) => {
      state.notes = action.payload;
      state.lastModified = new Date().toISOString();
    },
    setBackgroundColor: (state, action: PayloadAction<string>) => {
      state.backgroundColor = action.payload;
      state.lastModified = new Date().toISOString();
    },
    
    // Canvas reset
    resetCanvas: (state) => {
      state.elements = [];
      state.backgroundColor = '#ffffff';
      state.canvasShape = 'square';
      state.lastModified = new Date().toISOString();
    }
  }
});

export const {
  setCanvas,
  addElement,
  updateElement,
  deleteElement,
  duplicateElement,
  setDesignName,
  setSize,
  updateDimension,
  setCanvasShape,
  setQuantity,
  setFinish,
  setNotes,
  setBackgroundColor,
  resetCanvas
} = designSlice.actions;

export default designSlice.reducer;
